import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { healthCheckService, HealthCheckResult } from '../services/healthCheck';
import { useAuthStore } from './auth';

interface HealthState {
  isChecking: boolean;
  lastCheck: Date | null;
  result: HealthCheckResult | null;
  isIgnored: boolean;
  checkInterval: number | null;
}

export const useHealthCheckStore = defineStore('healthCheck', () => {
  // State
  const state = ref<HealthState>({
    isChecking: false,
    lastCheck: null,
    result: null,
    isIgnored: false,
    checkInterval: null
  });

  // Computed
  const hasIssues = computed(() => {
    return state.value.result?.status === 'warning' || 
           state.value.result?.status === 'unhealthy';
  });

  const visibleIssues = computed(() => {
    return hasIssues.value && !state.value.isIgnored;
  });

  const canIgnore = computed(() => {
    return state.value.result?.canIgnore || false;
  });

  const issueCount = computed(() => {
    return state.value.result?.failedIntegrations?.length || 0;
  });

  const errorIntegrations = computed(() => {
    return state.value.result?.failedIntegrations || [];
  });

  // Actions
  const checkHealth = async () => {
    const authStore = useAuthStore();
    
    // Kullanıcı giriş yapmamışsa kontrol yapma
    if (!authStore.isAuthenticated) {
      return;
    }

    // Zaten kontrol yapılıyorsa tekrar başlatma
    if (state.value.isChecking) {
      return;
    }

    state.value.isChecking = true;
    
    try {
      const result = await healthCheckService.checkIntegrationsHealth();
      state.value.result = result;
      state.value.lastCheck = new Date();
      
      // Yeni sonuç geldiğinde ignore durumunu sıfırla
      if (result.status === 'healthy') {
        state.value.isIgnored = false;
      }
    } catch (error) {
      console.error('Health check failed:', error);
    } finally {
      state.value.isChecking = false;
    }
  };

  const ignoreWarning = () => {
    if (canIgnore.value) {
      state.value.isIgnored = true;
    }
  };

  const startPeriodicCheck = (intervalMs: number = 180000) => {
    // Önceki interval varsa temizle
    stopPeriodicCheck();
    
    // İlk kontrolü yap
    checkHealth();
    
    // Periyodik kontrolü başlat
    state.value.checkInterval = window.setInterval(() => {
      checkHealth();
    }, intervalMs);
  };

  const stopPeriodicCheck = () => {
    if (state.value.checkInterval) {
      window.clearInterval(state.value.checkInterval);
      state.value.checkInterval = null;
    }
  };

  const reset = () => {
    stopPeriodicCheck();
    state.value = {
      isChecking: false,
      lastCheck: null,
      result: null,
      isIgnored: false,
      checkInterval: null
    };
  };

  return {
    // State
    state,
    
    // Computed
    hasIssues,
    visibleIssues,
    canIgnore,
    issueCount,
    errorIntegrations,
    
    // Actions
    checkHealth,
    ignoreWarning,
    startPeriodicCheck,
    stopPeriodicCheck,
    reset
  };
});